import { useCallback, useMemo, useState } from "react";

import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";

import {
  catalogLookupPage,
  catalogLookups,
  catalogManufacturerUsage,
  catalogTiers,
  createCatalogLookup,
  createCatalogTier,
  deleteCatalogLookup,
  renameCatalogLookup,
  updateCatalogTier,
} from "@/api/functions/catalog-admin";
import type {
  LookupDeleteInput,
  LookupNameInput,
  LookupRenameInput,
  TierCreateInput,
  TierUpdateInput,
} from "@/api/contracts/catalog-admin";
import type { LookupSelectFieldProps } from "@/components/ui/lookup-select-field";
import type { LookupKind, LookupOption } from "@/lib/catalog-admin";

// The catalog admin panels' data access: one read per lookup kind, one for
// tiers, and the writes that refresh them.
//
// The tier read is the one exception to "admin panels": #255 gave it a second
// caller on the items list, where a national editor's batch Mark as Core dialog
// fills its multi-select from it. Its key still reads `["admin", "catalog",
// "tiers"]` — the same rows, cached once for both callers, and renaming the key
// would only move where the surprise is. What that second caller changed is the
// gate (`_TIER_READ_GATE`, not `_ADMIN_GATE`) and `enabled`, both below. A third
// caller, the spec editor's Item Search island (#895), widened that gate to
// every catalog reader.
//
// Nothing polls here, unlike `admin-background-jobs.ts`: a lookup list changes
// only when someone on this screen changes it, so an interval would be requests
// spent to learn nothing. The freshness rules are otherwise that hook's, and for
// its reason — `staleTime: 0` and `refetchOnMount: "always"` mean the panel's
// first paint is never a cached snapshot from a previous visit. `gcTime` is left
// at its default: these lists are small and the three sections mount together,
// so keeping them briefly after unmount costs nothing and saves the re-read when
// an admin flips between tabs.
//
// **A write invalidates the unpaged read and *patches* the paged one.**
//
// The unpaged list — the admin tables and the filter sidebar — is re-read, as
// it always was. The paged namespace the inline menus read is not: it is edited
// in place, because that is what the donor does. Its three mutations invalidate
// a tag nothing provides (the list is `getObjectsFalcon(builder,
// "load-manufacturers")` providing `{type: "load-manufacturers"}`,
// `itemsApi.ts:416,500-508`; the writes invalidate `{type:
// "create-manufacturer"}` and siblings, `:417-419,520-521`), so no lookup write
// has ever re-read that list. The menu changes only by the local patch:
// appended to the foot on create (`CustomSelectWithPagination.tsx:169`), mapped
// in place on rename (`:146-152`), filtered out on delete (`:137-140`).
//
// Re-reading instead put the row wherever the server sorts it, and past a
// hundred rows put it nowhere at all: add a manufacturer to 1,254 finishes and
// the next menu you open does not list it. #412's parity review, round 5.
//
// **Each write invalidates its own kind's key and no other.** One shared
// `["admin", "catalog"]` invalidation would be shorter and would re-read all
// three lists plus the tiers on every rename — and, worse, would hide a
// mis-keyed read: a list that refreshes because something else was written looks
// correct until it is the only thing on screen.

/** What each read resolves to, inferred rather than restating the wire type. */
type LookupList = Awaited<ReturnType<typeof catalogLookups>>;
type TierList = Awaited<ReturnType<typeof catalogTiers>>;

/**
 * The key for one lookup kind's list. Exported because a second spelling of it
 * is how a create silently stops refreshing the table it just added a row to.
 */
export const catalogLookupsQueryKey = (kind: LookupKind) =>
  ["admin", "catalog", "lookups", kind] as const;

export const catalogTiersQueryKey = ["admin", "catalog", "tiers"] as const;

/**
 * The namespace every *paged* read of one kind lives under — #412's inline
 * lookup menus, which fetch a window at a time rather than the whole list.
 *
 * Separate from `catalogLookupsQueryKey` on purpose. The admin table and the
 * catalog filter sidebar need every alive row and page nothing; a menu holds
 * one 100-row window and a term. Two windows onto the same rows, so two cache
 * entries — sharing one would hand the admin table a single page and call it
 * the list.
 *
 * Exported as the prefix the writes invalidate: a create, rename or delete has
 * to refresh *every* term's list, not just the one the menu happens to be
 * showing, because the row it wrote may match a term the user types next.
 */
export const catalogLookupPagesQueryPrefix = (kind: LookupKind) =>
  ["admin", "catalog", "lookup-page", kind] as const;

/**
 * One term's paged list. The term is part of the key, which is what makes a
 * new term a new list starting at page one — the donor spends an effect on
 * that (`setOffset(0); setCurrentOptions([])` on `debouncedFilter` change,
 * `CustomSelectWithPagination.tsx:91-94`); here it is the cache's own
 * behavior, and there is no window of the old term's rows under the new term.
 *
 * **Not trimmed.** Whitespace is part of the term: the donor's `buildParams`
 * drops only the empty string, so `" "` really is sent as `name=%20` and
 * matches names containing a space (`services/common.ts:61-80`). Trimming here
 * made `" "` and `""` one cache entry, which is one question too few — #412's
 * parity review, finding 3.
 *
 * **Kind and term only, so two menus of the same kind would share one page
 * list.** Mount two Manufacturer menus at once and a page loaded in either
 * appears in both, scroll depth included. Nothing does today — the item edit
 * page and the Request Item dialog each mount manufacturer + finish +
 * material, and the Add Variant dialog's finish + material sit on a different
 * route from the edit page — and the donor has the mirror-image quirk (its
 * RTK Query key drops `offset` and keys on `filters` alone, so its two menus
 * would collide on one cache entry too, each keeping its own component-state
 * accumulator). Left as-is deliberately: per-instance keys would double the
 * reads whenever two same-kind menus did appear, which is not the donor's
 * behavior either. #412 decision round, D9 = a, 2026-09-15 — if you are here
 * because a new screen mounts two of one kind, this is the coupling you found.
 */
export const catalogLookupPagesQueryKey = (kind: LookupKind, search: string) =>
  [...catalogLookupPagesQueryPrefix(kind), search] as const;

export const catalogLookupsQueryOptions = (kind: LookupKind) =>
  queryOptions({
    queryKey: catalogLookupsQueryKey(kind),
    queryFn: async () => {
      const result = await catalogLookups({ data: { kind } });
      // The wire echoes the kind it answered for, and this is the one place
      // that echo is worth anything: the answer is about to be cached under
      // *this* kind's key. A mismatch means the dispatch sent the wrong
      // declaration, and the visible symptom would otherwise be finishes listed
      // under Materials — a wrong screen, not an error. Failing here turns that
      // into the panel's error notice instead.
      if (result.kind !== kind) {
        throw new Error(`The catalog answered for ${result.kind} when ${kind} was asked for.`);
      }
      return result;
    },
    staleTime: 0,
    refetchOnMount: "always",
  });

/**
 * The window the inline lookup menus read.
 *
 * The donor's `limit = 100` (`CustomSelectWithPagination.tsx:32`), and one of
 * ADR-0008 §6's allowed sizes — the server refuses anything else, so this
 * number is a contract rather than a tuning knob.
 */
export const LOOKUP_MENU_PAGE_SIZE = 100;

export const catalogLookupPagesQueryOptions = (kind: LookupKind, term: string) =>
  infiniteQueryOptions({
    queryKey: catalogLookupPagesQueryKey(kind, term),
    queryFn: async ({ pageParam }) => {
      const result = await catalogLookupPage({
        data: {
          kind,
          // Omitted rather than sent empty: a blank box is *no* term, which is
          // what the donor's `buildParams` does by dropping the key. Sending
          // `search: ""` would reach the same rows by asking a different
          // question, and the two would drift the first time either side
          // treated an empty term as a filter.
          ...(term ? { search: term } : {}),
          page: pageParam,
          pageSize: LOOKUP_MENU_PAGE_SIZE,
        },
      });
      // The unpaged read's check, for its reason — see
      // `catalogLookupsQueryOptions`.
      if (result.kind !== kind) {
        throw new Error(`The catalog answered for ${result.kind} when ${kind} was asked for.`);
      }
      return result;
    },
    initialPageParam: 1,
    /**
     * `hasNextPage`, derived from `total` exactly as the donor derives it from
     * ItemLib's `count` (`itemsApi.ts:476-487`) — so the observable stop
     * condition is the donor's, not a new one.
     *
     * Strictly `<`: 200 rows at 100 a page end on page 2, and offering page 3
     * would fetch an empty window that the observer would read as one more
     * last row and ask about again.
     */
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.pageSize < lastPage.total ? lastPage.page + 1 : undefined,
    staleTime: 0,
    refetchOnMount: "always",
    /**
     * **Dropped the moment nothing is reading it**, which is how a term change
     * empties the accumulator here.
     *
     * The donor keeps its pages in component state and clears them outright on
     * every debounced term change — `setOffset(0); setCurrentOptions([])`
     * (`CustomSelectWithPagination.tsx:91-94`). Here each term is its own cache
     * entry, so the equivalent of "cleared" is "not kept": switch terms and the
     * old entry goes inactive; with no retention it is gone, and coming back to
     * it — including back to the blank term, which is what closing the menu
     * does — starts again at page 1. Retaining it instead re-showed every page
     * a previous visit had scrolled through: 200 rows where modsy shows 100.
     */
    gcTime: 0,
  });

/** One manufacturer's usage — #803's read, which the editor's delete control
 *  asks before it offers a delete (#858). */
export const manufacturerUsageQueryKey = (id: number) =>
  ["admin", "catalog", "manufacturer-usage", id] as const;

/**
 * **Read afresh on every ask** (`staleTime: 0`, fetched with `fetchQuery`): the
 * counts decide whether the delete control opens a confirm or a refusal, and an
 * answer kept from an earlier click could offer a delete that someone has since
 * made impossible. The server's guard re-checks either way; this keeps the
 * dialog from contradicting it.
 */
export const manufacturerUsageQueryOptions = (id: number) =>
  queryOptions({
    queryKey: manufacturerUsageQueryKey(id),
    queryFn: () => catalogManufacturerUsage({ data: { id } }),
    staleTime: 0,
  });

export const catalogTiersQueryOptions = () =>
  queryOptions({
    queryKey: catalogTiersQueryKey,
    queryFn: () => catalogTiers(),
    staleTime: 0,
    refetchOnMount: "always",
  });

export function useCatalogLookups(kind: LookupKind) {
  return useQuery(catalogLookupsQueryOptions(kind));
}

/**
 * The alive tiers.
 *
 * `enabled` because the tier read is no longer the admin section's alone: the
 * batch Mark as Core dialog needs it too (#255), and that dialog is one of four
 * on a page most visits never open. Fetching on every visit to the items list
 * would spend a request per page load to fill a picker nobody asked for, so the
 * caller says when it wants it. Default `true` — the admin panel's three call
 * sites want it the moment they render.
 */
/**
 * One inline lookup menu's data, as the props `LookupSelectField` spreads.
 *
 * **The control stays query-agnostic**, which is the donor's own arrangement:
 * `CustomSelectWithPagination` takes its query as a `getQuery` prop
 * (`:31`) and knows nothing about where the rows come from. Here the props
 * arrive as one bundle so each call site spends a spread rather than six
 * lines, and so the one call site whose "list" is a single locked row —
 * `add-variant-dialog.tsx:330`, a variant inheriting its parent's
 * manufacturer — keeps passing `options` literally and pages nothing.
 *
 * The **term** lives here rather than in the control because it keys the read;
 * the control owns the 200ms debounce in front of it (its local filter needs
 * the same settled value) and reports the result through `onSearchSettled`.
 * That is the donor's split exactly: `useDebounce(filter, 200)` in the
 * control, `filters: { name: debouncedFilter }` handed to the query.
 */
/**
 * Exactly the props a `LookupSelectField` is spread with, and annotated so
 * that stays true.
 *
 * Every consumer writes `{...finishes}`, and a JSX spread is not checked for
 * excess properties — so renaming any of the five optional props on the
 * control leaves this hook returning the old name, compiling clean, and
 * silently dropping the paging, the spinner or the term. Naming the contract
 * here turns that into a type error at the hook. `Required`, because the five
 * are optional on the control — a call site may leave them off — and this hook
 * always supplies all six.
 */
type LookupMenu = Required<
  Pick<
    LookupSelectFieldProps,
    "options" | "loading" | "failed" | "hasMore" | "onLoadMore" | "onSearchSettled"
  >
>;

export function useCatalogLookupMenu(kind: LookupKind): LookupMenu {
  const [search, setSearch] = useState("");
  const { data, isFetching, isError, hasNextPage, fetchNextPage } = useInfiniteQuery(
    catalogLookupPagesQueryOptions(kind, search),
  );

  const options = useMemo(() => data?.pages.flatMap((page) => page.lookups) ?? [], [data]);

  // `fetchNextPage` unguarded: react-query refuses a second in-flight page
  // itself, and the donor's own guard is in the control — it declines to
  // *observe* the last row while a fetch is in flight
  // (`CustomSelectWithPagination.tsx:75`), which is where that guard is
  // restored, so it is not doubled here.
  const onLoadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  return {
    options,
    /** The donor's `isFetching` — true for the first read *and* for an
     * appended page, which is what draws the spinner under the rows already
     * shown rather than in place of them. */
    loading: isFetching,
    failed: isError,
    hasMore: hasNextPage,
    onLoadMore,
    onSearchSettled: setSearch,
  };
}

export function useCatalogTiers(enabled = true) {
  return useQuery({ ...catalogTiersQueryOptions(), enabled });
}

/** The cache entry behind one kind's paged read, as `setQueriesData` sees it.
 * `LookupPageResult` rather than `LookupPage`, which is the *wire* page over in
 * `server/data/internal-api/catalog-admin.ts`; one name for two things in one
 * import graph is how the wrong one gets imported. */
type LookupPageResult = Awaited<ReturnType<typeof catalogLookupPage>>;
type LookupPages = { pages: LookupPageResult[]; pageParams: unknown[] };

/**
 * Edit every cached page of one kind's menu read, in place.
 *
 * The donor's accumulator is component state and its writes splice it directly
 * (`CustomSelectWithPagination.tsx:137-152,169`). Here the accumulator is the
 * infinite query's page list, so the same splice is a `setQueriesData` over the
 * kind's namespace. Every term is patched, not just the active one: the donor
 * holds a single accumulator, and `gcTime: 0` means there is rarely more than
 * one entry anyway.
 *
 * `total` is deliberately left alone — the donor's `count` comes from the last
 * read and no write updates it, so `hasNextPage` answers the same question on
 * both sides.
 */
const patchLookupPages = async (
  queryClient: QueryClient,
  kind: LookupKind,
  edit: (rows: LookupOption[], isLastPage: boolean) => LookupOption[],
) => {
  // **Cancel first, or an appended page lands on top of this patch.**
  // `infiniteQueryBehavior` snapshots the page list when the fetch *starts*
  // and writes `[...snapshot, newPage]` when it resolves, so a write made
  // while page 2 is in the air is simply gone when page 2 arrives — add a
  // finish mid-scroll and the trigger names a row the reopened menu does not
  // have. The donor cannot hit this: its accumulator is component state and
  // its append is the functional `setCurrentOptions(prev => [...prev,
  // ...newResults])` (`CustomSelectWithPagination.tsx:67`), which composes
  // with the splice instead of replacing it.
  //
  // Cancelling is the closest Atlas gets: the flight is abandoned, the patch
  // stands, and the observer — still watching a last row that is still in
  // view — asks for that page again. Both survive, the page a beat later.
  // #412, cross-review run 1.
  //
  // **Only a read that has something to patch**, because cancelling one that
  // does not is unrecoverable: `setQueriesData` below no-ops on an empty
  // cache entry, and nothing re-asks — the query is left pending with no
  // observer event to restart it, `refetchOnMount` needs a *new* mount, and
  // the invalidation under this is the unpaged key's. Add a finish while the
  // menu's first page is still in the air and the menu you reopen says "No
  // matches." over a catalog of a hundred. The donor loses the row here too —
  // its append reads a stale `currentOptions` (`:171`) and the first page
  // replaces rather than appends (`:63-64`) — but it loses the *row*, not the
  // list. Letting the read land is that outcome. #412, cross-review run 2.
  await queryClient.cancelQueries({
    queryKey: catalogLookupPagesQueryPrefix(kind),
    predicate: (query) => query.state.data !== undefined,
  });
  queryClient.setQueriesData<LookupPages>(
    { queryKey: catalogLookupPagesQueryPrefix(kind) },
    (cached) =>
      cached && {
        ...cached,
        pages: cached.pages.map((page, index) => ({
          ...page,
          lookups: edit(page.lookups, index === cached.pages.length - 1),
        })),
      },
  );
};

/**
 * The lookup writes' options, taking the client explicitly so the cache work is
 * testable without rendering a provider — the shape
 * `triggerBackgroundJobMutationOptions` established.
 *
 * Touching the cache on success only: a refused write changed nothing on the
 * server, and re-reading after one would replace the row the user is still
 * editing.
 */
export function createLookupMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogLookup>>, Error, LookupNameInput> {
  return {
    mutationFn: (data) => createCatalogLookup({ data }),
    onSuccess: async (created, input) => {
      // Appended to the foot of what is loaded, out of sort order, as
      // `setCurrentOptions([...currentOptions, newOption])` does (`:169`). The
      // last page only: append to each and a menu three pages deep lists the
      // row three times.
      await patchLookupPages(queryClient, input.kind, (rows, isLastPage) =>
        isLastPage ? [...rows, created] : rows,
      );
      await queryClient.invalidateQueries({ queryKey: catalogLookupsQueryKey(input.kind) });
    },
  };
}

export function renameLookupMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<Awaited<ReturnType<typeof renameCatalogLookup>>, Error, LookupRenameInput> {
  return {
    mutationFn: (data) => renameCatalogLookup({ data }),
    onSuccess: async (renamed, input) => {
      // Mapped where it already sits, so the row keeps its index under its new
      // name however that name would sort (`:146-152`).
      await patchLookupPages(queryClient, input.kind, (rows) =>
        rows.map((row) => (row.id === renamed.id ? renamed : row)),
      );
      await queryClient.invalidateQueries({ queryKey: catalogLookupsQueryKey(input.kind) });
    },
  };
}

export function deleteLookupMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogLookup>>, Error, LookupDeleteInput> {
  return {
    mutationFn: (data) => deleteCatalogLookup({ data }),
    onSuccess: async (_result, input) => {
      // Filtered out (`:137-140`).
      await patchLookupPages(queryClient, input.kind, (rows) =>
        rows.filter((row) => row.id !== input.id),
      );
      await queryClient.invalidateQueries({ queryKey: catalogLookupsQueryKey(input.kind) });
    },
  };
}

export function createTierMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogTier>>, Error, TierCreateInput> {
  return {
    mutationFn: (data) => createCatalogTier({ data }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogTiersQueryKey }),
  };
}

export function updateTierMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogTier>>, Error, TierUpdateInput> {
  return {
    mutationFn: (data) => updateCatalogTier({ data }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogTiersQueryKey }),
  };
}

// There is deliberately no tier-delete mutation: ADR-0005 §8 gives tiers no
// delete verb, and there is no route to call.

export function useCreateCatalogLookup() {
  return useMutation(createLookupMutationOptions(useQueryClient()));
}

export function useRenameCatalogLookup() {
  return useMutation(renameLookupMutationOptions(useQueryClient()));
}

export function useDeleteCatalogLookup() {
  return useMutation(deleteLookupMutationOptions(useQueryClient()));
}

export function useCreateCatalogTier() {
  return useMutation(createTierMutationOptions(useQueryClient()));
}

export function useUpdateCatalogTier() {
  return useMutation(updateTierMutationOptions(useQueryClient()));
}

/**
 * The three writes a `LookupSelectField` can start, curried by kind.
 *
 * One hook rather than three, because a screen that lets you edit a lookup
 * inline always wants all three: the Request Item dialog and the item edit page
 * had the identical fifteen lines, which is how the two would eventually come
 * to disagree about what "delete" means on a select.
 *
 * Each returns the promise the select awaits, and answers with the row itself
 * (`lookupWire`) — exactly what the select needs to select, so there is nothing
 * to reconcile at the call site. The mutations invalidate their kind's list on
 * success, so the options behind the menu catch up on their own.
 *
 * The delete is the donor's delete-by-clearing-the-name, which the select
 * reaches when a rename is submitted empty — and, on the owner's D3 ruling,
 * only after the select has asked. See the note in `lookup-select-field.tsx`.
 */
export function useLookupEditing() {
  const create = useCreateCatalogLookup();
  const rename = useRenameCatalogLookup();
  const remove = useDeleteCatalogLookup();

  return {
    createOption:
      (kind: LookupKind) =>
      (name: string): Promise<LookupOption> =>
        create.mutateAsync({ kind, name }),
    renameOption:
      (kind: LookupKind) =>
      (id: number, name: string): Promise<LookupOption> =>
        rename.mutateAsync({ kind, id, name }),
    deleteOption:
      (kind: LookupKind) =>
      async (id: number): Promise<void> => {
        await remove.mutateAsync({ kind, id });
      },
  };
}

export type { LookupList, TierList };
